import type {
  AnnounceArtist,
  AnnounceArtistInput,
  AnnounceAttachment,
  AnnounceAttachmentInput,
  CreateAnnounceDetailInput,
  Maybe,
  Mutation,
  Query,
} from '@/generated/graphql';
import { useConnectionStore } from '@/stores/connection';
import { useRootStore } from '@/stores/root';
import { defineStore } from 'pinia';
import type { FormRequest } from 'vuniform';
import type RequestResult from '@/types/RequestResult';
import * as apolloCore from '@apollo/client/core';

const { gql } = apolloCore;

export enum ButtonTextEnum {
  TICKETS = 'Tickets',
  FREE = 'Free',
  POSTPONED = 'Postponed',
  CANCELLED = 'Cancelled',
  CUSTOM = 'Custom',
}

export enum TicketLinkModeEnum {
  USE_VP_LINK = 'Use VP link',
  USE_VP_LINK_NEW_TAB = 'Use VP link (new tab)',
  USE_EXTERNAL_LINK = 'Use external link',
  NO_TICKET_LINK = 'No ticket link',
}

export interface AnnounceDetail {
  id: number;
  event_id: number;
  promoter: string;
  event_name: string;
  support: string;
  description: string;
  website_url: string;
  facebook_url: string;
  twitter_url: string;
  instagram_url: string;
  ticketsUrl: string;
  defaultTicketsUrl: string;
  defaultTicketsUrlNewTab: string;
  ticketsUrlMode?: string;
  images: string[];
  announceAt: string;
  highlightedImageIdx: number;
  buttonText: string;
  buttonTextAdditional?: string;
  on_sale_at: string;
  triggered_at: string;
  status: string;
  tags: string[] | string; // XXX: Temporary, until token input is fixed
  attachments: AnnounceAttachment[];
  announceArtists: AnnounceArtist[];
}

export interface Event {
  id: number;
  name: string;
  date: string;
  startTime: string;
  doorTime: string;
  timezone: string;
  announceDetail?: AnnounceDetail;
}

interface EventState {
  selectedEvent?: Event;
  selectedEventLoading: boolean;
}

const GET_EVENT_QUERY = gql`
  query GetEvent($eventId: Int!) {
    event(id: $eventId) {
      id
      date
      doorTime
      startTime
      endTime
      name
      timezone
      announceDetail {
        announceAt
        createdAt
        description
        eventId
        eventName
        facebookUrl
        highlightedImageIdx
        id
        images
        instagramUrl
        onSaleAt
        promoter
        status
        support
        tags
        ticketsUrl
        defaultTicketsUrl
        defaultTicketsUrlNewTab
        triggeredAt
        twitterUrl
        updatedAt
        websiteUrl
        artists
        buttonText
        attachments {
          attachment {
            id
            url
            status
          }
          highlighted
        }
        announceArtists {
          id
          name
          instagram
          facebook
          twitter
          website
          spotify
          youtube
          lastfm
          applemusic
          wikipedia
          songkick
          bandcamp
        }
      }
    }
  }
`;

export interface SaveAnnounceParams {
  eventId: number;
  promoter: string;
  eventName: string;
  description: string;
  announceAt: string;
  highlightedImageIdx: Maybe<number>;
  buttonText: string;
  tags: string[] | string; // XXX: Temporary, until token input is fixed
  attachments?: AnnounceAttachmentInput[];
  announceArtists?: AnnounceArtistInput[];
}

const SAVE_ANNOUNCE_MUTATION = gql`
  mutation SaveAnnounce($announceInput: CreateAnnounceDetailInput!) {
    createAnnounceDetail(input: $announceInput) {
      announceDetail {
        id
        eventId
        eventName
        description
        promoter
        highlightedImageIdx
        tags
        announceAt
        artists
        ticketsUrl
        defaultTicketsUrl
        defaultTicketsUrlNewTab
        buttonText
        attachments {
          attachment {
            id
            url
            status
          }
          highlighted
        }
        announceArtists {
          id
          name
          instagram
          facebook
          twitter
          website
          spotify
          youtube
          lastfm
          applemusic
          wikipedia
          songkick
          bandcamp
        }
      }
      errors
    }
  }
`;

export const useEventStore = defineStore('event', {
  state: (): EventState => ({
    selectedEvent: undefined,
    selectedEventLoading: false,
  }),

  getters: {
    getSelectedEventId(): number | undefined {
      const rootStore = useRootStore();
      const id = parseInt(<string>rootStore.route?.params?.eventId);
      return isNaN(id) ? undefined : id;
    },

    getSelectedEvent(state: EventState): Event | undefined {
      return (state.selectedEventLoading || undefined) && state.selectedEvent;
    },
  },

  actions: {
    async fetchSelectedEvent() {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      const selectedEventId = this.getSelectedEventId;

      if (this.getSelectedEvent) {
        return;
      }

      if (!selectedEventId) {
        throw 'No selected event id.';
      }

      if (this.selectedEventLoading) {
        // TODO: wait until finished loading and check if the required event is loaded
        return;
      }

      this.selectedEventLoading = true;
      try {
        const { data } = await apiClient.query<Query>({
          query: GET_EVENT_QUERY,
          variables: {
            eventId: selectedEventId,
          },
          fetchPolicy: 'network-only',
        });
        this.selectedEvent = data.event as Event;
        if (this.selectedEvent.announceDetail?.tags) {
          this.selectedEvent = {
            ...this.selectedEvent,
            announceDetail: {
              ...this.selectedEvent.announceDetail,
              tags: (this.selectedEvent.announceDetail.tags as string[]).join(','),
            },
          };
        }
        // } catch (e: any) {
        //   throw e;
        if (this.selectedEvent.announceDetail) {
          const buttonText = this?.selectedEvent?.announceDetail?.buttonText;
          const isButtonTextCustom = !(Object.keys(ButtonTextEnum) as (keyof typeof ButtonTextEnum)[]).find(
            key => ButtonTextEnum[key] === buttonText,
          );
          if (buttonText) {
            if (isButtonTextCustom) {
              this.selectedEvent.announceDetail.buttonText = ButtonTextEnum.CUSTOM;
              this.selectedEvent.announceDetail.buttonTextAdditional = buttonText;
            } else {
              this.selectedEvent.announceDetail.buttonText = buttonText;
              this.selectedEvent.announceDetail.buttonTextAdditional = undefined;
            }
          } else {
            this.selectedEvent.announceDetail.buttonText = ButtonTextEnum.TICKETS;
          }

          this.setSelectedEventTicketLinkMode();
        }
      } finally {
        this.selectedEventLoading = false;
      }
    },

    async saveAnnounceDetail(payload: FormRequest<SaveAnnounceParams>): Promise<RequestResult<unknown>> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: SAVE_ANNOUNCE_MUTATION,
          variables: {
            announceInput: <CreateAnnounceDetailInput>{
              announceDetail: {
                ...payload.params,
                tags: (payload.params.tags as string)
                  .split(',')
                  .map(v => v.trim())
                  .filter(v => v),
              },
            },
          },
        });
        if (!data?.createAnnounceDetail?.errors) {
          this.selectedEvent = {
            ...this.selectedEvent,
            announceDetail: {
              ...(data?.createAnnounceDetail?.announceDetail ?? {}),
            },
          };
          if (this.selectedEvent?.announceDetail?.tags) {
            this.selectedEvent.announceDetail.tags = (this.selectedEvent.announceDetail.tags as string[]).join(', ');
          }

          this.setSelectedEventTicketLinkMode();
        } else {
          return { errors: data.createAnnounceDetail.errors };
        }
      } finally {
        //
      }

      return {};
    },

    setSelectedEventTicketLinkMode() {
      const announceDetail = this?.selectedEvent?.announceDetail;

      if (!announceDetail) {
        return;
      }

      const { ticketsUrl, defaultTicketsUrl, defaultTicketsUrlNewTab } = announceDetail;

      if (!ticketsUrl) {
        announceDetail.ticketsUrlMode = TicketLinkModeEnum.NO_TICKET_LINK;
      } else if (ticketsUrl === defaultTicketsUrl) {
        announceDetail.ticketsUrlMode = TicketLinkModeEnum.USE_VP_LINK;
      } else if (ticketsUrl === defaultTicketsUrlNewTab) {
        announceDetail.ticketsUrlMode = TicketLinkModeEnum.USE_VP_LINK_NEW_TAB;
      } else {
        announceDetail.ticketsUrlMode = TicketLinkModeEnum.USE_EXTERNAL_LINK;
      }
    },
  },
});
